import { Alert, Button, Input, Spin, Tag, message } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CATCH_BOARD_MAX_LINKED_VENUES,
  CATCH_BOARD_VENUE_SEARCH_DEBOUNCE_MS,
  CATCH_BOARD_VENUE_SEARCH_MIN_CHARS,
} from './catchBoardConstants'
import { useCatchBoardDuplicateVenueWarnings } from './useCatchBoardDuplicateVenueWarnings'
import { fetchCatchBoardDuplicateVenueWarnings } from 'src/utils/catchBoardApi'
import {
  linkCatchBoardVenue,
  setCatchBoardPrimaryVenue,
  unlinkCatchBoardVenue,
} from 'src/utils/catchBoardFormHelpers'
import {
  formatCatchBoardDuplicateVenueAlsoOn,
  formatCatchBoardDuplicateVenueWarning,
  hasCatchBoardDuplicateVenueWarnings,
} from 'src/utils/catchBoardDuplicateVenues'
import { CATCH_BOARD_TOOLTIPS } from 'src/utils/catchBoardTooltips'
import { searchCatchBoardVenues } from 'src/utils/catchBoardVenueSearch'
import styled from 'styled-components/macro'

/**
 * Search Spartacus venues, link/unlink, and set primary for a Catch Board deal.
 * Warns (does not block) when a linked venue is already on another open deal.
 */
const CatchBoardDealVenuesSection = ({
  form,
  onFormChange,
  disabled = false,
  excludeDealId = null,
  enabled = true,
}) => {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const requestIdRef = useRef(0)

  const linkedVenues = form?.linkedVenues || []
  const primaryVenueId = form?.primaryVenueId
    ? String(form.primaryVenueId)
    : null
  const atLimit = linkedVenues.length >= CATCH_BOARD_MAX_LINKED_VENUES

  const linkedVenueIds = useMemo(
    () => linkedVenues.map(row => row.venueId),
    [linkedVenues],
  )

  const venueNamesById = useMemo(() => {
    const names = {}
    linkedVenues.forEach(venue => {
      if (venue?.venueId) {
        names[String(venue.venueId)] =
          venue.name || venue.venueName || `Venue ${venue.venueId}`
      }
    })
    return names
  }, [linkedVenues])

  const { warnings, loading: warningsLoading } =
    useCatchBoardDuplicateVenueWarnings({
      venueIds: linkedVenueIds,
      excludeDealId,
      enabled: enabled && !disabled,
    })

  useEffect(() => {
    const trimmed = String(search || '').trim()
    if (trimmed.length < CATCH_BOARD_VENUE_SEARCH_MIN_CHARS) {
      setResults([])
      setSearching(false)
      setSearchError(null)
      return undefined
    }

    const requestId = ++requestIdRef.current
    const timer = setTimeout(async () => {
      setSearching(true)
      setSearchError(null)
      try {
        const venues = await searchCatchBoardVenues(trimmed)
        if (requestId !== requestIdRef.current) {
          return
        }
        setResults(venues)
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return
        }
        setResults([])
        setSearchError(error.message || 'Venue search failed')
      } finally {
        if (requestId === requestIdRef.current) {
          setSearching(false)
        }
      }
    }, CATCH_BOARD_VENUE_SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [search])

  const handleLink = venue => {
    if (disabled) {
      return
    }
    if (atLimit) {
      message.warning(
        `You can link at most ${CATCH_BOARD_MAX_LINKED_VENUES} venues`,
      )
      return
    }
    if (linkedVenues.some(row => row.venueId === String(venue.venueId))) {
      message.info('Venue already linked')
      return
    }
    onFormChange?.(linkCatchBoardVenue(form, venue))
    setSearch('')
    setResults([])

    // Toast on explicit link only (handoff warns before the modal opens).
    ;(async () => {
      try {
        const nextWarnings = await fetchCatchBoardDuplicateVenueWarnings({
          venueIds: [venue.venueId],
          excludeDealId,
        })
        const warning = (nextWarnings || []).find(
          row => String(row.venueId) === String(venue.venueId),
        )
        if (!warning?.deals?.length) {
          return
        }
        const venueName =
          venue.name || venue.venueName || `Venue ${venue.venueId}`
        message.warning(
          `${venueName}: ${formatCatchBoardDuplicateVenueAlsoOn(warning)}`,
        )
      } catch {
        // Warn-only — linking already succeeded.
      }
    })()
  }

  const handleUnlink = venueId => {
    if (disabled) {
      return
    }
    onFormChange?.(unlinkCatchBoardVenue(form, venueId))
  }

  const handleSetPrimary = venueId => {
    if (disabled) {
      return
    }
    onFormChange?.(setCatchBoardPrimaryVenue(form, venueId))
  }

  const linkedIds = new Set(linkedVenues.map(row => row.venueId))
  const showDuplicateAlert = hasCatchBoardDuplicateVenueWarnings(warnings)

  return (
    <Section data-testid='catch-board-deal-venues'>
      <SectionTitle>Linked venues</SectionTitle>
      <SectionHint>
        Search Spartacus to link venues. Primary venue is the default for notes
        and context.
      </SectionHint>

      <SearchRow>
        <Input
          value={search}
          disabled={disabled || atLimit}
          placeholder={
            atLimit
              ? `Maximum ${CATCH_BOARD_MAX_LINKED_VENUES} venues linked`
              : 'Search venues by name…'
          }
          onChange={event => setSearch(event.target.value)}
          data-testid='catch-board-venue-search'
        />
        {searching ? <Spin size='small' /> : null}
      </SearchRow>

      {searchError ? (
        <SearchError data-testid='catch-board-venue-search-error'>
          {searchError}
        </SearchError>
      ) : null}

      {results.length > 0 ? (
        <ResultsList data-testid='catch-board-venue-search-results'>
          {results.map(venue => {
            const alreadyLinked = linkedIds.has(String(venue.venueId))
            return (
              <ResultRow key={venue.venueId}>
                <ResultName title={venue.name}>{venue.name}</ResultName>
                <Button
                  size='small'
                  type='link'
                  disabled={disabled || alreadyLinked || atLimit}
                  onClick={() => handleLink(venue)}
                  data-testid={`catch-board-venue-link-${venue.venueId}`}
                >
                  {alreadyLinked ? 'Linked' : 'Link'}
                </Button>
              </ResultRow>
            )
          })}
        </ResultsList>
      ) : null}

      {!searching &&
      String(search || '').trim().length >= CATCH_BOARD_VENUE_SEARCH_MIN_CHARS &&
      results.length === 0 &&
      !searchError ? (
        <EmptyResults data-testid='catch-board-venue-search-empty'>
          No venues matched “{String(search).trim()}”
        </EmptyResults>
      ) : null}

      {showDuplicateAlert ? (
        <Alert
          type='warning'
          showIcon
          style={{ marginTop: 10 }}
          data-testid='catch-board-deal-venue-duplicates'
          message='Venue also on other open deals'
          description={
            <>
              <WarnOnlyHint data-testid='catch-board-deal-venue-duplicates-hint'>
                {CATCH_BOARD_TOOLTIPS.duplicateVenueWarn}
              </WarnOnlyHint>
              <WarningList>
                {warnings.map(warning => (
                  <li key={warning.venueId || 'venue'}>
                    {formatCatchBoardDuplicateVenueWarning(warning, {
                      venueNamesById,
                    })}
                  </li>
                ))}
              </WarningList>
            </>
          }
        />
      ) : null}

      {warningsLoading && linkedVenues.length > 0 && !showDuplicateAlert ? (
        <CheckingHint data-testid='catch-board-deal-venue-duplicates-loading'>
          Checking for other open deals…
        </CheckingHint>
      ) : null}

      {linkedVenues.length === 0 ? (
        <EmptyLinked data-testid='catch-board-venues-empty'>
          No venues linked yet — optional until you’re ready.
        </EmptyLinked>
      ) : (
        <LinkedList data-testid='catch-board-venues-linked'>
          {linkedVenues.map(venue => {
            const isPrimary = primaryVenueId === venue.venueId
            const venueWarning = warnings.find(
              warning => String(warning.venueId) === String(venue.venueId),
            )
            return (
              <LinkedRow
                key={venue.venueId}
                data-testid={`catch-board-venue-linked-${venue.venueId}`}
              >
                <LinkedMain>
                  <LinkedName title={venue.name}>{venue.name}</LinkedName>
                  {isPrimary ? (
                    <Tag color='green' data-testid='catch-board-venue-primary-tag'>
                      Primary
                    </Tag>
                  ) : null}
                </LinkedMain>
                <LinkedActions>
                  {!isPrimary ? (
                    <Button
                      size='small'
                      onClick={() => handleSetPrimary(venue.venueId)}
                      disabled={disabled}
                      data-testid={`catch-board-venue-set-primary-${venue.venueId}`}
                    >
                      Set primary
                    </Button>
                  ) : null}
                  <Button
                    size='small'
                    type='danger'
                    ghost
                    onClick={() => handleUnlink(venue.venueId)}
                    disabled={disabled}
                    data-testid={`catch-board-venue-unlink-${venue.venueId}`}
                  >
                    Unlink
                  </Button>
                </LinkedActions>
                {venueWarning?.deals?.length ? (
                  <AlsoOnHint
                    data-testid={`catch-board-venue-also-on-${venue.venueId}`}
                  >
                    {formatCatchBoardDuplicateVenueAlsoOn(venueWarning)}
                  </AlsoOnHint>
                ) : null}
              </LinkedRow>
            )
          })}
        </LinkedList>
      )}
    </Section>
  )
}

const Section = styled.div`
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const SectionHint = styled.p`
  margin: 4px 0 10px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SearchError = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #a94442;
`

const ResultsList = styled.div`
  margin-top: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  max-height: 160px;
  overflow-y: auto;
  background: #fff;
`

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:last-child {
    border-bottom: none;
  }
`

const ResultName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
`

const EmptyResults = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const EmptyLinked = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const CheckingHint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const WarnOnlyHint = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
`

const WarningList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
`

const LinkedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`

const LinkedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: #f7f9f8;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
`

const LinkedMain = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const LinkedName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
`

const LinkedActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const AlsoOnHint = styled.div`
  flex: 1 0 100%;
  font-size: 12px;
  line-height: 1.35;
  color: #8a6d3b;
`

export default CatchBoardDealVenuesSection
